/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { AxisBottom, AxisLeft, TickFormatter } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleLinear, scaleBand } from "@visx/scale";
import { LinePath } from "@visx/shape";
import { NumberValue } from "d3-scale";
import dayjs from "dayjs";
import { If, Then } from "react-if";
import styled from "styled-components";
import { theme } from "@nordcloud/gnui";
import {
  ClusterConsumedResource,
  ConsumedResource,
  Maybe,
  StorageReadWriteResource,
} from "~/generated/graphql";
import { getMinMax } from "~/components/Charts/utils";
import { dateFormat } from "~/constants";
import { periodDayJs } from "~/services/customers";
import { generateNumericArray, isEmpty, isNotEmpty } from "~/tools";

export const CHART_COLORS = {
  used: theme.color.support.indigo,
  requested: theme.color.support.purple,
};

type ConsumedResourcesJoined = {
  __typename?:
    | "ClusterConsumedResource"
    | "ConsumedResource"
    | "StorageReadWriteResource";
  date?: Maybe<string>;
  used?: Maybe<string>;
  requested?: Maybe<string>;
  read?: Maybe<string>;
  write?: Maybe<string>;
};

export type ConsumptionChartProps = {
  width: number;
  height: number;
  data: ConsumedResourcesJoined[];
  axisFormatter?: TickFormatter<NumberValue>;
  isNode?: boolean;
};

const Margin = {
  x: 40,
  y: 40,
};

const formatDate = (date: string) =>
  dayjs(date).format(dateFormat.dayMonthShort);

const defaultAxisFormatter = String;

function sortByDate(
  a: ClusterConsumedResource | ConsumedResource | StorageReadWriteResource,
  b: ClusterConsumedResource | ConsumedResource | StorageReadWriteResource
) {
  return (
    dayjs(a.date || new Date()).date() - dayjs(b.date || new Date()).date()
  );
}

const getDateRange = () => {
  const jsDate = periodDayJs();
  const start = jsDate.startOf("month");
  const end = jsDate.endOf("month");
  const diff = end.diff(start, "day") + 1;
  return generateNumericArray(diff).map((index) =>
    start.add(index, "day").format(dateFormat.shortDate)
  );
};

const getConsumptionRange = (data: ConsumedResourcesJoined[]) => {
  const readArray = data.map((item) => item.read ?? 0);
  const writeArray = data.map((item) => item.write ?? 0);

  const requestArray = data.map((item) => item.requested ?? 0);
  const usedArray = data.map((item) => item.used ?? 0);
  const values = [
    ...readArray.map(Number),
    ...writeArray.map(Number),
    ...usedArray.map(Number),
    ...requestArray.map(Number),
  ];
  if (isEmpty(values)) {
    return [0, 500];
  }
  return getMinMax(values);
};

const Message = styled.text`
  fill: ${theme.color.text.text02};
  font-size: ${theme.fontSizes.sm};
  opacity: 0.6;
`;

export function ConsumptionChart({
  width,
  height,
  data,
  axisFormatter = defaultAxisFormatter,
  isNode = false,
}: ConsumptionChartProps) {
  const xMax = width - Margin.x;
  const yMax = height - Margin.y;

  const xScale = React.useMemo(
    () =>
      scaleBand<string>({
        range: [1, xMax],
        domain: getDateRange(),
      }),
    [xMax]
  );

  const yScale = React.useMemo(() => {
    return scaleLinear<number>({
      range: [yMax, 0],
      round: true,
      domain: getConsumptionRange(data),
    });
  }, [yMax, data]);

  const noData = isEmpty(data);

  const colorOptions = React.useMemo(
    () => ({
      strokeOpacity: noData ? 0.2 : 1,
      strokeColor: noData
        ? theme.color.border.border01
        : theme.color.border.focus,
      strokeLabelColor: noData
        ? theme.color.border.border01
        : theme.color.text.text02,
    }),
    [noData]
  );

  const sortedData = [...data].sort(sortByDate);

  return (
    <div>
      <svg width={width} height={height}>
        <Group top={Margin.y / 2} left={Margin.x}>
          <GridRows
            scale={yScale}
            width={xMax}
            strokeDasharray="1,3"
            stroke={theme.color.border.focus}
            strokeOpacity={colorOptions.strokeOpacity}
            pointerEvents="none"
            numTicks={5}
          />
          <AxisLeft
            top={0}
            scale={yScale}
            stroke={colorOptions.strokeColor}
            tickStroke="transparent"
            numTicks={4}
            tickFormat={axisFormatter}
            tickLabelProps={() => ({
              fill: colorOptions.strokeLabelColor,
              fontSize: 8,
              textAnchor: "end",
            })}
          />
          <Group left={8}>
            <AxisBottom
              top={yMax}
              scale={xScale}
              stroke={colorOptions.strokeColor}
              tickStroke={colorOptions.strokeColor}
              numTicks={4}
              tickFormat={formatDate}
              left={-8}
              tickLabelProps={() => ({
                fill: colorOptions.strokeLabelColor,
                fontSize: 8,
                textAnchor: "middle",
              })}
            />
          </Group>
          <If condition={isEmpty(data)}>
            <Then>
              <Group
                left={(width - Margin.x - 120) / 2}
                top={(height - Margin.y) / 2}
              >
                <Message>no data available</Message>
              </Group>
            </Then>
          </If>
          <If condition={isNotEmpty(data)}>
            <Then>
              <Group left={8}>
                <LinePath
                  strokeWidth={2}
                  data={sortedData}
                  strokeOpacity={1}
                  stroke={CHART_COLORS.used}
                  shapeRendering="geometricPrecision"
                  markerMid="url(#marker-circle)"
                  markerStart="url(#marker-circle)"
                  markerEnd="url(#marker-circle)"
                  x={(d) => xScale(d.date ?? "") ?? 0}
                  y={(d) =>
                    d.read
                      ? yScale(Number(d.read)) ?? 0
                      : yScale(Number(d.used)) ?? 0
                  }
                />
              </Group>
              <If condition={!isNode}>
                <Then>
                  <Group left={8}>
                    <LinePath
                      strokeWidth={2}
                      data={sortedData}
                      strokeOpacity={1}
                      stroke={CHART_COLORS.requested}
                      shapeRendering="geometricPrecision"
                      markerMid="url(#marker-circle)"
                      markerStart="url(#marker-circle)"
                      markerEnd="url(#marker-circle)"
                      x={
                        sortedData.findIndex(
                          (object) => object.write ?? object.requested
                        ) >= 0
                          ? (d) => xScale(d.date ?? "") ?? 0
                          : 0
                      }
                      y={(d) =>
                        d.write
                          ? yScale(Number(d.write)) ?? 0
                          : yScale(Number(d.requested)) ?? 0
                      }
                    />
                  </Group>
                </Then>
              </If>
            </Then>
          </If>
        </Group>
      </svg>
    </div>
  );
}
